import { FloatingPortal } from "@floating-ui/react";
import * as styles from "./ModalV2.styles.ts";
import {
  useEffect,
  type PropsWithChildren,
  createContext,
  useState,
  useRef,
  type PointerEvent,
} from "react";
import { CloseButton } from "src/components/CloseButton/CloseButton.tsx";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { LevelUp } from "src/components/LevelUp/LevevUp.tsx";

type ModalProps = PropsWithChildren<{
  hideCloseButton?: boolean;
  closeButtonPosition?: "inside" | "half-outside";
  onClose?(): void;
}>;

export const ModalV2Context = createContext<Record<string, string>>({});

export const ModalV2 = ({
  children,
  hideCloseButton = false,
  closeButtonPosition = "inside",
  onClose,
}: ModalProps) => {
  const isPointerDownInside = useRef(false);
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) =>
      ev.key === "Escape" && onClose?.();

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const [context] = useState({});

  const handleModalContentPointerDown = () => {
    isPointerDownInside.current = true;
  };

  const handleBackgroundPointerUp = () => {
    if (!isPointerDownInside.current) {
      onClose?.();
    }
    isPointerDownInside.current = false;
  };

  const handleModalContentPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    isPointerDownInside.current = false;
  };

  return (
    <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
      <ModalV2Context.Provider value={context}>
        <div css={styles.root} onPointerUp={handleBackgroundPointerUp}>
          <div
            onPointerDown={handleModalContentPointerDown}
            onPointerUp={handleModalContentPointerUp}
            css={styles.container}
          >
            {!hideCloseButton && (
              <CloseButton
                css={styles.closeButton({ position: closeButtonPosition })}
                onClick={onClose}
              />
            )}
            <LevelUp type={"modals"}>{children}</LevelUp>
          </div>
        </div>
      </ModalV2Context.Provider>
    </FloatingPortal>
  );
};
